import { useEffect } from "react";
import { useBlocker } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../primitives/alert-dialog";

// Warns before leaving an editor with unsaved changes — blocks in-app
// navigation (react-router) with a confirm, and guards browser close/refresh via
// beforeunload. `allow` lets a host permit intentional hops (e.g. the catalogue
// editor stepping out to the configurator) without a warning.

export function UnsavedChangesGuard({
  when,
  allow,
}: {
  when: boolean;
  allow?: (nextPathname: string) => boolean;
}) {
  const capabilities = usePlatformCapabilities();
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      nextLocation.pathname !== currentLocation.pathname &&
      !(allow?.(nextLocation.pathname) ?? false),
  );

  useEffect(() => capabilities.warnBeforeUnload(when), [capabilities, when]);

  return (
    <AlertDialog
      open={blocker.state === "blocked"}
      onOpenChange={(open) => {
        if (!open) blocker.reset?.();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. If you leave now they won't be saved or go
            live.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => blocker.reset?.()}>
            Stay
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => blocker.proceed?.()}>
            Leave
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
